import { setForceFullSampling, whenTelemetryReady } from '../services/telemetry';

/**
 * Drop-in replacement for the old `NewRelic()` helper (New Relic Browser
 * agent), now backed by Grafana Faro shipping through `/api/tel/proxy`.
 * Keeps the exact same call surface — `noticeError` / `addPageAction` /
 * `setUserId` / `setCustomAttribute` — so every existing call site only
 * needed an import rename, not a rewrite.
 *
 * Faro's own `dedupe` + `ignoreErrors` config (see `services/telemetry.ts`)
 * already cover exact-duplicate and known-noisy-pattern error spam, so
 * `noticeError` doesn't need to re-implement either. What Faro *can't*
 * protect against is a single named event firing at high, sustained
 * frequency with different payloads each time — that's fixed at the source
 * (reduce the call frequency where the event is fired), not here.
 * `MAX_EVENTS_PER_MINUTE` below is only a last-resort circuit breaker
 * against a runaway bug, not a normal-usage limit.
 */
const MAX_EVENTS_PER_MINUTE = 120;
const MAX_ERRORS_PER_MINUTE = 120;
const RATE_WINDOW_MS = 60_000;

/**
 * Events and errors get SEPARATE budgets on purpose. A single shared counter
 * lets high-frequency instrumentation starve error reporting: `SocketLatency`
 * fires from `useTimesync` on every pong, and a reconnect stacks more ping
 * trains on top — so on exactly the flaky classroom network you most want
 * diagnostics from, the latency events would eat the budget and the connect
 * errors from that same incident would be dropped silently.
 *
 * Both remain last-resort circuit breakers against a runaway bug, not
 * normal-usage limits; a genuinely too-chatty event is fixed at its source.
 */
const makeRateLimiter = (maxPerMinute: number) => {
  let timestamps: number[] = [];
  return () => {
    const now = Date.now();
    timestamps = timestamps.filter(t => now - t < RATE_WINDOW_MS);
    if (timestamps.length >= maxPerMinute) {
      return false;
    }
    timestamps.push(now);
    return true;
  };
};

const withinEventBudget = makeRateLimiter(MAX_EVENTS_PER_MINUTE);
const withinErrorBudget = makeRateLimiter(MAX_ERRORS_PER_MINUTE);

/**
 * Keys whose values must never leave the browser, matched case-insensitively
 * at ANY depth. Errors drag credentials along more often than is obvious: an
 * AxiosError carries the whole request `config`, headers included, so
 * `catchHandler` reporting a failed API call would otherwise ship the user's
 * `Authorization: Bearer …` and session cookie straight to Grafana.
 */
const SENSITIVE_KEY_PATTERN = /(authorization|cookie|token|password|secret|credential|api[-_]?key|apikey|jwt|bearer|signature)/i;

/**
 * Axios attaches the full request config to every error, including headers and
 * the request body. Only the fields identifying WHICH request failed are worth
 * keeping — the rest is credentials and noise.
 */
const AXIOS_CONFIG_SAFE_KEYS = ['url', 'method', 'baseURL', 'timeout'];

/** Fields that are pure internals and can be large or cyclic. */
const DROPPED_KEYS = ['request'];

const REDACTED = '[redacted]';
const MAX_DEPTH = 4;
const MAX_STRING_LENGTH = 2_000;
const MAX_ARRAY_ITEMS = 20;

/**
 * Strips credentials out of anything on its way to Grafana, and bounds its
 * size. Applied centrally here rather than at call sites, because the risky
 * payloads are the ones nobody chose to send — the incidental fields hanging
 * off a thrown error.
 */
const sanitizeValue = (value: unknown, key?: string, depth = 0): unknown => {
  if (key && SENSITIVE_KEY_PATTERN.test(key)) return REDACTED;
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH
      ? `${value.slice(0, MAX_STRING_LENGTH)}…[truncated]`
      : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'function') return undefined;
  if (typeof value !== 'object') return String(value);

  if (depth >= MAX_DEPTH) return '[depth limit]';

  // `config.data` is the request body, which can hold anything the user typed
  // — including a password — as an ALREADY-SERIALISED string that per-key
  // redaction cannot see into. So narrow the whole object to an allowlist
  // rather than trying to clean it. Handled here rather than while iterating a
  // parent so it applies at the top level too: once an AxiosError has been
  // spread, `config` is a top-level key of the attributes object.
  if (key === 'config' && !Array.isArray(value)) {
    const config = value as Record<string, unknown>;
    return Object.fromEntries(
      AXIOS_CONFIG_SAFE_KEYS.filter(
        safeKey => config[safeKey] !== undefined,
      ).map(safeKey => [safeKey, sanitizeValue(config[safeKey], safeKey, depth + 1)]),
    );
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map(item => sanitizeValue(item, undefined, depth + 1));
  }

  const result: Record<string, unknown> = {};
  for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
    if (DROPPED_KEYS.includes(childKey)) continue;

    result[childKey] = sanitizeValue(childValue, childKey, depth + 1);
  }
  return result;
};

const toStringAttributes = (
  attributes: { [id: string]: unknown } = {},
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(attributes).map(([key, rawValue]) => {
      const value = sanitizeValue(rawValue, key);
      if (value === null || value === undefined) return [key, ''];
      // Nested objects (an axios `response`, a socket payload) would otherwise
      // stringify to "[object Object]".
      if (typeof value === 'object') return [key, safeStringify(value)];
      return [key, String(value)];
    }),
  );

/**
 * Enumerable own fields of whatever was thrown.
 *
 * Deliberately applies to real Errors too. Faro serialises only
 * `name`/`message`/`stack`, so an Error subclass's extra fields — axios's
 * `code` and `response`, an APIError's `status` — reach Grafana only if they
 * are lifted into context here.
 *
 * Spreading is exactly the right tool: `message` and `stack` are NON-enumerable
 * own properties, so they are skipped automatically (they travel on the Error
 * itself), while the custom fields come through. An earlier revision read that
 * same non-enumerability as a reason to skip Errors entirely and returned `{}`
 * for them, which silently dropped every one of those fields — they had
 * survived the older `Object.assign` merge this replaced.
 */
const enumerableFieldsOf = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return { ...(value as Record<string, unknown>) };
};

/**
 * Turns anything that was thrown into an Error worth reporting.
 *
 * `new Error(String(value))` is not good enough: socket.io and axios hand us
 * plain objects, and `String({...})` is `"[object Object]"` — which is what
 * roughly twenty `noticeError(res.error)` call sites in
 * `frontendServices/gameAPI.ts` were reporting. Lifting the payload's own
 * `message`/`name` keeps those errors distinguishable from each other in
 * Grafana instead of collapsing them into one meaningless group.
 *
 * The synthesised Error's stack points here rather than at the throw site,
 * which is unavoidable — a plain object never carried one. That is precisely
 * why the payload's fields are also attached as context.
 */
const toReportableError = (value: unknown): Error => {
  if (value instanceof Error) return value;

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const candidate = (value as { message?: unknown }).message;
    const message =
      typeof candidate === 'string' && candidate ? candidate : safeStringify(value);
    const error = new Error(message);
    const name = (value as { name?: unknown }).name;
    if (typeof name === 'string' && name) error.name = name;
    return error;
  }

  return new Error(String(value));
};

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

export interface TelemetryClient {
  addPageAction: (
    name: string,
    attributes?: { [id: string]: string | number | boolean },
  ) => void;
  noticeError: (error: unknown, context?: { [id: string]: unknown }) => void;
  setUserId: (uid: string | null) => void;
  setCustomAttribute: (name: string, value: string | number | boolean | null) => void;
  /**
   * Re-includes THIS session in Faro's export regardless of the global
   * `NEXT_PUBLIC_TELEMETRY_SAMPLING_RATE`. Faro's session instrumentation
   * stamps every session with an `isSampled` attribute at session-start time
   * (random draw against that rate) and its `beforeSend` hook silently drops
   * any event whose CURRENT session attributes aren't `isSampled: 'true'` —
   * so a session sampled out at page-load stays invisible for its entire
   * lifetime unless something flips that attribute back on.
   *
   * Call this the moment a session enters a flow you cannot afford to lose
   * to random sampling. Every event pushed AFTER this call ships, regardless
   * of the original draw; events already pushed before it are unaffected
   * (the hook snapshots session attributes at push time, not send time).
   *
   * Routed through `services/telemetry.ts`'s `setForceFullSampling`, not
   * just a `setSession` attribute poke — see that flag's own comment for why
   * a plain attribute overwrite alone gets silently reverted.
   */
  forceFullSession: () => void;
}

/**
 * Use this when not in the react context.
 *
 * Always returns a client — calls made before the SDK has loaded are queued
 * and replayed (see `whenTelemetryReady`), which is what keeps the old
 * synchronous `NewRelic()?.` call sites correct. The existing `?.` at those
 * call sites is now redundant but harmless, so they were left alone.
 */
export const Telemetry = (): TelemetryClient => {
  return {
    addPageAction: (name, attributes) => {
      if (!withinEventBudget()) return;
      const stringAttributes = toStringAttributes(attributes);
      whenTelemetryReady(faro => faro.api.pushEvent(name, stringAttributes));
    },
    noticeError: (error, context) => {
      if (!withinErrorBudget()) return;
      // Normalise NOW, not at replay time: a thrown value can be mutated
      // between the call and the SDK finishing its load.
      const normalized = toReportableError(error);
      // Faro drops anything hanging off a custom Error subclass (it only
      // serialises name/message/stack), so the extra context every call site
      // attaches has to be handed over explicitly or it is lost — this is
      // what surfaces as `error_context_*` on the Loki log line.
      // A non-Error payload's own fields would otherwise be lost with it —
      // gameAPI.ts passes raw socket.io `res.error` objects straight in.
      const stringContext = toStringAttributes({
        ...enumerableFieldsOf(error),
        ...context,
      });
      whenTelemetryReady(faro =>
        faro.api.pushError(normalized, {
          context: Object.keys(stringContext).length ? stringContext : undefined,
        }),
      );
    },
    setUserId: uid => {
      whenTelemetryReady(faro => {
        if (uid) {
          faro.api.setUser({ id: uid });
        } else {
          faro.api.resetUser();
        }
      });
    },
    setCustomAttribute: (name, value) => {
      whenTelemetryReady(faro => {
        // Must preserve the existing session's `id` (and anything else on
        // it) — Faro's SessionInstrumentation fires a `session_start` event
        // any time the session meta's `id` changes, so passing only
        // `{ attributes }` here blanks `id` out to `undefined` on every call,
        // which reads as "a new session" and produces a spurious
        // session_start per call.
        const currentSession = faro.api.getSession();
        faro.api.setSession({
          ...currentSession,
          attributes: {
            ...currentSession?.attributes,
            [name]: value === null ? '' : String(value),
          },
        });
      });
    },
    forceFullSession: () => {
      // Deliberately NOT deferred: this flag feeds the sampler Faro consults
      // at session start, so setting it before init is what makes the
      // override apply to the very first draw rather than a later re-draw.
      setForceFullSampling();
      // `isSampled` is the exact attribute name Faro's own
      // SessionInstrumentation reads in its beforeSend hook — same merge
      // shape as setCustomAttribute, for the same session-id-preservation
      // reason. Also nudges Faro's metas listener to reconcile the session
      // against storage right now rather than waiting for the next natural
      // resample point — that reconciliation is what actually applies the
      // override above.
      whenTelemetryReady(faro => {
        const currentSession = faro.api.getSession();
        faro.api.setSession({
          ...currentSession,
          attributes: { ...currentSession?.attributes, isSampled: 'true' },
        });
      });
    },
  };
};

/**
 * Use this in react components to send metrics.
 */
export const useTelemetry = () => {
  return {
    telemetry: Telemetry(),
  };
};

/**
 * Error type for when objects are thrown instead of proper Error instances
 */
export class ObjectError extends Error {
  public readonly attributes: { [key: string]: unknown };

  constructor(attributes: { [key: string]: unknown } = {}) {
    super('An object error was thrown');
    this.name = 'ObjectError';
    this.attributes = attributes;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ObjectError);
    }
  }
}

/**
 * Error type for when object errors cannot be parsed
 */
export class UnparsableObjectError extends Error {
  public readonly attributes: { [key: string]: unknown };

  constructor(attributes: { [key: string]: unknown } = {}) {
    super(
      'An object error was thrown, but was unable to be parsed by telemetryNoticeError',
    );
    this.name = 'UnparsableObjectError';
    this.attributes = attributes;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UnparsableObjectError);
    }
  }
}

/**
 * Reports an error plus any amount of loose context. The ~40 call sites across
 * the app pass anything from a bare string to an axios error plus three
 * context objects, so the signature stays deliberately permissive.
 *
 * The rule: a real Error among the arguments is what gets reported (keeping
 * its name, message and stack), and everything else becomes Faro `context`.
 */
export function telemetryNoticeError(...args: unknown[]) {
  try {
    // A real Error among the arguments is the ONLY thing carrying a usable
    // stack, so it must be what gets reported.
    //
    // This used to fall through to the object-merge path below whenever a
    // context argument was present, e.g. the ~40 call sites shaped like
    // `telemetryNoticeError(err, 'connect_error')` in gameAPI.ts. That path
    // merges arguments with `Object.assign`, which copies only ENUMERABLE own
    // properties — and `Error`'s `message` and `stack` are both
    // non-enumerable. So the original error was silently discarded and Faro
    // received an ObjectError reading "An object error was thrown", with a
    // stack pointing at this function rather than at the throw site. Every
    // such error also grouped together in Grafana, since they all shared that
    // one message.
    const primaryError = args.find(arg => arg instanceof Error) as Error | undefined;
    const contextArgs = args.filter(arg => arg !== primaryError);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: { [key: string]: any } = {};
    let argCounter = 1;

    // appends the remaining arguments into a single context object
    contextArgs.forEach((arg: unknown) => {
      if (typeof arg === 'string') {
        result[`arg${argCounter++}`] = arg;
      } else if (arg && typeof arg === 'object' && !Array.isArray(arg)) {
        Object.assign(result, arg);
      }
    });

    if (primaryError) {
      Telemetry()?.noticeError(primaryError, result);
      return;
    }

    // Nothing thrown was a real Error — synthesise one so the context still
    // reaches Grafana. `noticeError` lifts `message`/`name` off the payload,
    // so these stay distinguishable from one another.
    const objectError = new ObjectError(result);
    Telemetry()?.noticeError(objectError, result);
  } catch (error) {
    // Create UnparsableObjectError when parsing fails
    const unparsableError = new UnparsableObjectError({
      message: args,
      telemetryNoticeError: 'Could not parse error object',
    });
    Telemetry()?.noticeError(unparsableError, {
      telemetryNoticeError: 'Could not parse error object',
    });
  }
}
